import type { InfiniteData } from '@tanstack/react-query'
import type { Message, MessagePage } from './api'

/** Pages of the chat in the query cache: `pages[0]` holds the latest messages, then older and older. */
export type ChatData = InfiniteData<MessagePage, number | null>

/**
 * Every loaded message, oldest first, without duplicates.
 * @param data - Cached pages.
 * @returns The messages to show.
 */
export function flatten(data: ChatData): Message[] {
  const seen = new Set<number>()
  const messages: Message[] = []
  for (const page of [...data.pages].reverse()) {
    for (const message of page.messages) {
      if (seen.has(message.id)) continue
      seen.add(message.id)
      messages.push(message)
    }
  }
  return messages.sort((a, b) => a.id - b.id)
}

/**
 * Adds messages to the latest page. The sender gets their own message twice
 * (the API answer and Realtime): only the first one counts.
 * @param data - Cached pages.
 * @param messages - Messages that arrived.
 * @returns Updated pages, or the same object when nothing is new.
 */
export function addMessages(data: ChatData, messages: Message[]): ChatData {
  const known = new Set(data.pages.flatMap((page) => page.messages.map((message) => message.id)))
  const fresh = messages.filter((message) => !known.has(message.id))
  const [latest, ...older] = data.pages
  if (fresh.length === 0 || !latest) return data
  const merged = [...latest.messages, ...fresh].sort((a, b) => a.id - b.id)
  return { ...data, pages: [{ ...latest, messages: merged }, ...older] }
}

/**
 * Removes a deleted message.
 * @param data - Cached pages.
 * @param id - The deleted message.
 * @returns Updated pages.
 */
export function removeMessage(data: ChatData, id: number): ChatData {
  return {
    ...data,
    pages: data.pages.map((page) => ({ ...page, messages: page.messages.filter((message) => message.id !== id) })),
  }
}

/**
 * Whether a message is already loaded.
 * @param data - Cached pages.
 * @param id - The message.
 * @returns `true` when present.
 */
export function hasMessage(data: ChatData | undefined, id: number): boolean {
  return data?.pages.some((page) => page.messages.some((message) => message.id === id)) ?? false
}

/**
 * Id of the newest loaded message.
 * @param data - Cached pages.
 * @returns The id, or `undefined` when the chat is empty.
 */
export function newestId(data: ChatData | undefined): number | undefined {
  return data?.pages[0]?.messages.at(-1)?.id
}
